import { supabase } from "@/integrations/supabase/client";
import { supabaseSelectWithRetry } from "@/lib/supabaseRetry";

// Service pour calculer de vraies statistiques basées sur la base de données
export const statisticsService = {
  // Calcule les vraies statistiques de connexions
  async getConnectionStats() {
    try {
      // Compter les vraies connexions (requêtes acceptées)
      const { count: connectionsCount } = await supabaseSelectWithRetry(
        () => supabase
          .from('connection_requests')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'accepted')
      );

      // Compter les messages réels
      const { count: messagesCount } = await supabaseSelectWithRetry(
        () => supabase
          .from('chat_messages')
          .select('*', { count: 'exact', head: true })
      );

      // Compter les conversations actives
      const { count: conversationsCount } = await supabaseSelectWithRetry(
        () => supabase
          .from('conversations')
          .select('*', { count: 'exact', head: true })
          .eq('is_archived', false)
      );

      return {
        totalConnections: connectionsCount || 0,
        totalMessages: messagesCount || 0,
        activeConversations: conversationsCount || 0
      };
    } catch (error) {
      console.error('Error fetching connection stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getConnectionStats] Error details:', errorMsg);
      return {
        totalConnections: 0,
        totalMessages: 0,
        activeConversations: 0
      };
    }
  },

  // ⚠️ DEPRECATED: Utiliser useMemberGrowth() hook à la place
  // Cette fonction ne prend pas en compte member_count_history et peut retourner des données incorrectes
  // Préférer useMemberGrowth() qui utilise la fonction PostgreSQL calculate_member_growth()
  async getMemberGrowthStats() {
    try {
      // Compter le total de membres actuels (actifs)
      const { count: totalToday } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'actif')
      );

      // Compter les nouveaux membres des 30 derniers jours
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: newMembersThisMonth } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'actif')
          .gte('created_at', thirtyDaysAgo.toISOString())
      );

      // Compter le total de membres il y a 30 jours (créés AVANT cette date)
      const { count: totalLastMonth } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'actif')
          .lt('created_at', thirtyDaysAgo.toISOString())
      );

      // Calculer le pourcentage de croissance: (Total aujourd'hui - Total il y a 30 jours) / Total il y a 30 jours * 100
      let growthPercentage = 0;
      if (totalLastMonth && totalLastMonth > 0) {
        growthPercentage = Math.round((((totalToday || 0) - totalLastMonth) / totalLastMonth) * 100);
      } else if (totalToday && totalToday > 0) {
        // Nouvelle communauté : si aucun membre il y a 30 jours mais des membres aujourd'hui
        growthPercentage = 100;
      }

      return {
        totalMembers: totalToday || 0,
        newMembersThisMonth: newMembersThisMonth || 0,
        growthPercentage,
        isGrowthPositive: growthPercentage > 0
      };
    } catch (error) {
      console.error('Error fetching member growth stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getMemberGrowthStats] Error details:', errorMsg);
      return {
        totalMembers: 0,
        newMembersThisMonth: 0,
        growthPercentage: 0,
        isGrowthPositive: false
      };
    }
  },

  // Calcule les vraies statistiques d'événements
  async getEventStats() {
    try {
      // Compter les événements à venir
      const now = new Date().toISOString();
      const { count: upcomingEvents } = await supabaseSelectWithRetry(
        () => supabase
          .from('events')
          .select('*', { count: 'exact', head: true })
          .gte('start_date', now)
      );

      // Compter les inscriptions totales
      const { count: totalRegistrations } = await supabaseSelectWithRetry(
        () => supabase
          .from('event_registrations')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'confirmed')
      );

      return {
        upcomingEvents: upcomingEvents || 0,
        totalRegistrations: totalRegistrations || 0
      };
    } catch (error) {
      console.error('Error fetching event stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getEventStats] Error details:', errorMsg);
      return {
        upcomingEvents: 0,
        totalRegistrations: 0
      };
    }
  },

  // Calcule les vraies statistiques d'opportunités
  async getOpportunityStats() {
    try {
      // Compter les opportunités actives
      const { count: activeOpportunities } = await supabaseSelectWithRetry(
        () => supabase
          .from('opportunities')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active')
      );

      // Compter les candidatures
      const { count: totalApplications } = await supabaseSelectWithRetry(
        () => supabase
          .from('opportunity_applications')
          .select('*', { count: 'exact', head: true })
      );

      return {
        activeOpportunities: activeOpportunities || 0,
        totalApplications: totalApplications || 0
      };
    } catch (error) {
      console.error('Error fetching opportunity stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getOpportunityStats] Error details:', errorMsg);
      return {
        activeOpportunities: 0,
        totalApplications: 0
      };
    }
  },

  // Calcule les vraies statistiques d'activité et de croissance
  async getActivityStats() {
    try {
      // Récupérer l'activité des 7 derniers jours
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data: recentConnections } = await supabaseSelectWithRetry(
        () => supabase
          .from('connection_requests')
          .select('created_at')
          .eq('status', 'accepted')
          .gte('created_at', sevenDaysAgo.toISOString())
      );

      const { data: recentMessages } = await supabaseSelectWithRetry(
        () => supabase
          .from('chat_messages')
          .select('created_at')
          .gte('created_at', sevenDaysAgo.toISOString())
      );

      // Organiser par jour
      const weeklyActivity = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        const dayConnections = recentConnections?.filter(c => 
          new Date(c.created_at).toDateString() === date.toDateString()
        ).length || 0;
        const dayMessages = recentMessages?.filter(m => 
          new Date(m.created_at).toDateString() === date.toDateString()
        ).length || 0;

        return {
          day: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
          connections: dayConnections,
          messages: dayMessages
        };
      });

      return { weeklyActivity };
    } catch (error) {
      console.error('Error fetching activity stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getActivityStats] Error details:', errorMsg);
      return { weeklyActivity: [] };
    }
  },

  // Calcule l'évolution de la croissance mensuelle (CUMULATIF)
  async getGrowthEvolution() {
    try {
      const growthData = [];
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        // ✅ Compte TOUS les membres créés jusqu'à la fin de ce mois (cumul)
        const { count } = await supabaseSelectWithRetry(
          () => supabase
            .from('club_members')
            .select('*', { count: 'exact', head: true })
            .lte('created_at', endOfMonth.toISOString())
        );

        console.log(`📊 Croissance ${date.toLocaleDateString('fr-FR', { month: 'short' })}:`, count);

        growthData.push({
          month: date.toLocaleDateString('fr-FR', { month: 'short' }),
          members: count || 0
        });
      }

      console.log('📊 Growth data fetched:', growthData);
      return { growthData };
    } catch (error) {
      console.error('Error fetching growth evolution:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getGrowthEvolution] Error details:', errorMsg);
      return { growthData: [] };
    }
  },

  // Calcule les statistiques détaillées des membres
  async getDetailedMemberStats() {
    try {
      // Compter tous les membres
      const { count: totalMembers } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('*', { count: 'exact', head: true })
      );

      // Compter les membres actifs (ayant une interaction récente)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: activeMembers } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('*', { count: 'exact', head: true })
          .gte('last_interaction', thirtyDaysAgo.toISOString())
      );

      // Compter les membres premium
      const { count: premiumMembers } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('*', { count: 'exact', head: true })
          .eq('subscription_type', 'premium')
      );

      // Nouveaux membres ce mois
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: newThisMonth } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString())
      );

      return {
        totalMembers: totalMembers || 0,
        activeMembers: activeMembers || 0,
        premiumMembers: premiumMembers || 0,
        newThisMonth: newThisMonth || 0,
        activeMembersPercentage: totalMembers ? Math.round(((activeMembers || 0) / totalMembers) * 100) : 0,
        premiumMembersPercentage: totalMembers ? Math.round(((premiumMembers || 0) / totalMembers) * 100) : 0
      };
    } catch (error) {
      console.error('Error fetching detailed member stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getDetailedMemberStats] Error details:', errorMsg);
      return {
        totalMembers: 0,
        activeMembers: 0,
        premiumMembers: 0,
        newThisMonth: 0,
        activeMembersPercentage: 0,
        premiumMembersPercentage: 0
      };
    }
  },

  // Calcule les statistiques de compétences réelles
  async getSkillsStats() {
    try {
      const { data: members } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('skills')
          .not('skills', 'is', null)
      );

      const skillCounts: Record<string, number> = {};
      members?.forEach(member => {
        if (member.skills && Array.isArray(member.skills)) {
          member.skills.forEach(skill => {
            // ✅ Meilleur nettoyage des compétences
            const cleanSkill = skill?.trim();
            if (cleanSkill && cleanSkill.length > 0) {
              skillCounts[cleanSkill] = (skillCounts[cleanSkill] || 0) + 1;
            }
          });
        }
      });

      const topSkills = Object.entries(skillCounts)
        .sort(([,a], [,b]) => (b as number) - (a as number))
        .slice(0, 8)
        .map(([skill, count]) => ({ skill, count }));

      console.log('🎯 Skills stats:', topSkills);
      return { topSkills };
    } catch (error) {
      console.error('Error fetching skills stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getSkillsStats] Error details:', errorMsg);
      return { topSkills: [] };
    }
  },

  // Calcule les statistiques géographiques réelles
  async getGeographicStats() {
    try {
      const { data: members } = await supabaseSelectWithRetry(
        () => supabase
          .from('club_members')
          .select('country')
          .not('country', 'is', null)
          .neq('country', '')
          .neq('country', 'a') // Exclure les données corrompues
      );

      const countryCounts = {};
      members?.forEach(member => {
        if (member.country && member.country.trim() && member.country !== 'a') {
          countryCounts[member.country] = (countryCounts[member.country] || 0) + 1;
        }
      });

      const topCountries = Object.entries(countryCounts)
        .sort(([,a], [,b]) => (b as number) - (a as number))
        .slice(0, 5)
        .map(([country, count]) => ({ country, count }));

      return { topCountries };
    } catch (error) {
      console.error('Error fetching geographic stats:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [statisticsService.getGeographicStats] Error details:', errorMsg);
      return { topCountries: [] };
    }
  }
};